import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import { z } from 'zod'
import { apiRequest } from '@/shared/api'
import { type CursorPage, nextCursor } from '@/shared/lib'
import { EmployeeProjectsSchema, EmployeeSchema, EmployeeWorkloadSchema } from '../model/types'

export const employeeKeys = {
  list: ['employee', 'list'] as const,
  workload: ['employee', 'workload'] as const,
  projects: (id: string) => ['employee', 'projects', id] as const,
}

// Список сотрудников для пикеров. Мелкий справочник — без курсора (`{ data }`).
export const employeesQueryOptions = queryOptions({
  queryKey: employeeKeys.list,
  queryFn: async () => {
    const body = await apiRequest<{ data: unknown[] }>({ method: 'GET', url: '/employees' })
    return z.array(EmployeeSchema).parse(body.data)
  },
  staleTime: 5 * 60_000,
})

// Реестр «Сотрудники»: штат постранично (keyset по ФИО), у каждого — числа его
// нагрузки. Employee-only: заказчику бэк отвечает 403.
export const employeeWorkloadQueryOptions = infiniteQueryOptions({
  queryKey: employeeKeys.workload,
  queryFn: async ({ pageParam }) => {
    const body = await apiRequest<CursorPage<unknown>>({
      method: 'GET',
      url: '/employees/projects',
      query: { ...(pageParam ? { cursor: pageParam } : {}), limit: 50 },
    })
    return {
      items: z.array(EmployeeWorkloadSchema).parse(body.items),
      next_cursor: body.next_cursor ?? null,
    }
  },
  initialPageParam: undefined as string | undefined,
  getNextPageParam: nextCursor,
})

// Карточка сотрудника: он сам и его проекты в разрезе роли. Точечный запрос —
// страница открывается по прямой ссылке, не через прокачку реестра.
export const employeeProjectsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: employeeKeys.projects(id),
    queryFn: async () => {
      const body = await apiRequest<unknown>({ method: 'GET', url: `/employees/${id}/projects` })
      return EmployeeProjectsSchema.parse(body)
    },
  })
